import type {CreativeEngine} from "@cesdk/cesdk-js";
import {all, create} from "mathjs";
import {CALCULATOR_META_KEY} from "../constants.ts";
import {normalizeDynamicValueType, readWindowDynamicVariableEntry, type DynamicValueType} from "./dynamicVariables.ts";

export type CalculatorOutputType = "number" | "currency" | "percent";

export type CalculatorMeta = {
    enabled: boolean;
    expression: string;
    outputType: CalculatorOutputType;
    decimals: number;
    useThousandsSeparator: boolean;
    currencySymbol: string;
    prefix: string;
    suffix: string;
    varName?: string;
};

export type TextVariableReferenceKind = "numeric" | "display";

export type TextVariableDescriptor = {
    name: string;
    referenceKind: TextVariableReferenceKind;
};

export type ParsedCalculatorExpression = {
    hasDelimitedExpression: boolean;
    expression: string;
    delimiter: "doubleBrace" | "legacyDollar" | "brace" | null;
};

export type DelimitedCalculatorBlock = {
    expression: string;
    fullMatch: string;
    index: number;
    endIndex: number;
};

type DynamicVariableState = {
    value: string;
    valueType?: DynamicValueType;
};

const TEXT_BLOCK_TYPE = "//ly.img.ubq/text";
const MAX_DECIMALS = 8;
const CALCULATOR_PASS_LIMIT = 5;
const RESERVED_SYMBOLS = new Set(["e", "pi", "i", "Infinity", "NaN", "true", "false", "null", "undefined"]);
const EMPTY_TEXT_BLOCK_PLACEHOLDER = " ";
const EMPTY_TEXT_BLOCK_PLACEHOLDER_META_KEY = "idecide/empty-text-placeholder";

export const CALCULATOR_DELIMITER_OPEN = "{{";
export const CALCULATOR_DELIMITER_CLOSE = "}}";
export const CALCULATOR_DELIMITER_SOFT_BREAK = "\u2063";
const LEGACY_CALCULATOR_DELIMITER_OPEN = "${";
const LEGACY_CALCULATOR_DELIMITER_CLOSE = "}$";
const DOUBLE_BRACE_CALCULATOR_BLOCK_PATTERN = /\{(?:\u2063)?\{([\s\S]*?)\}(?:\u2063)?\}/g;

const math = create(all, {});
try {
    // Disable high-risk dynamic functions when evaluating user-entered expressions.
    math.import(
        {
            import: () => {
                throw new Error("Function import is disabled");
            },
            createUnit: () => {
                throw new Error("Function createUnit is disabled");
            },
            reviver: () => {
                throw new Error("Function reviver is disabled");
            }
        },
        {override: true}
    );
} catch {
    // Ignore in case function signatures differ between mathjs versions.
}

export const defaultCalculatorMeta = (): CalculatorMeta => ({
    enabled: false,
    expression: "",
    outputType: "number",
    decimals: 2,
    useThousandsSeparator: true,
    currencySymbol: "$",
    prefix: "",
    suffix: "",
    varName: ""
});

export const withCalculatorDefaults = (meta?: Partial<CalculatorMeta> | null): CalculatorMeta => {
    const base = defaultCalculatorMeta();
    if (!meta) return base;
    return {
        enabled: meta.enabled ?? base.enabled,
        expression: String(meta.expression ?? base.expression),
        outputType: normalizeOutputType(meta.outputType),
        decimals: clampDecimals(meta.decimals ?? base.decimals),
        useThousandsSeparator: meta.useThousandsSeparator ?? base.useThousandsSeparator,
        currencySymbol: String(meta.currencySymbol ?? base.currencySymbol),
        prefix: String(meta.prefix ?? base.prefix),
        suffix: String(meta.suffix ?? base.suffix),
        varName: String(meta.varName ?? base.varName)
    };
};

export const isTextBlock = (engine: CreativeEngine, blockId: number): boolean => {
    try {
        return engine.block.getType?.(blockId) === TEXT_BLOCK_TYPE;
    } catch {
        return false;
    }
};

const hasEmptyTextBlockPlaceholder = (engine: CreativeEngine, blockId: number): boolean => {
    try {
        return engine.block.getMetadata?.(blockId, EMPTY_TEXT_BLOCK_PLACEHOLDER_META_KEY) === "1";
    } catch {
        return false;
    }
};

const setEmptyTextBlockPlaceholder = (engine: CreativeEngine, blockId: number, active: boolean): void => {
    try {
        engine.block.setMetadata?.(blockId, EMPTY_TEXT_BLOCK_PLACEHOLDER_META_KEY, active ? "1" : "0");
    } catch {}
};

export const getTextBlockText = (engine: CreativeEngine, blockId: number): string => {
    try {
        const raw = String(engine.block.getString?.(blockId, "text/text") ?? "");
        if (raw === EMPTY_TEXT_BLOCK_PLACEHOLDER && hasEmptyTextBlockPlaceholder(engine, blockId)) {
            return "";
        }
        return raw;
    } catch {
        return "";
    }
};

export const setTextBlockText = (engine: CreativeEngine, blockId: number, value: string): void => {
    setEmptyTextBlockPlaceholder(engine, blockId, false);
    try {
        engine.block.setString?.(blockId, "text/text", String(value ?? ""));
    } catch {}
};

export const setTextBlockTextPreservingEmpty = (engine: CreativeEngine, blockId: number, value: string): void => {
    const next = String(value ?? "");
    if (next === "") {
        setEmptyTextBlockPlaceholder(engine, blockId, true);
        try {
            engine.block.setString?.(blockId, "text/text", EMPTY_TEXT_BLOCK_PLACEHOLDER);
        } catch {}
        return;
    }

    setEmptyTextBlockPlaceholder(engine, blockId, false);
    try {
        engine.block.setString?.(blockId, "text/text", next);
    } catch {}
};

const collectDelimitedCalculatorBlocks = (text: string, pattern: RegExp): DelimitedCalculatorBlock[] => {
    const out: DelimitedCalculatorBlock[] = [];
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) != null) {
        const fullMatch = String(match[0] ?? "");
        if (!fullMatch) {
            pattern.lastIndex += 1;
            continue;
        }
        const index = Number(match.index ?? 0);
        out.push({
            expression: String(match[1] ?? "").trim(),
            fullMatch,
            index,
            endIndex: index + fullMatch.length
        });
    }
    return out;
};

export const getDelimitedCalculatorBlocks = (text: string): DelimitedCalculatorBlock[] => {
    const raw = String(text ?? "");
    return collectDelimitedCalculatorBlocks(raw, DOUBLE_BRACE_CALCULATOR_BLOCK_PATTERN);
};

export const hasDelimitedCalculatorBlocks = (text: string): boolean =>
    getDelimitedCalculatorBlocks(text).length > 0;

export const replaceDelimitedCalculatorBlocks = (
    text: string,
    replacer: (expression: string, block: DelimitedCalculatorBlock, blockIndex: number) => string
): string => {
    const raw = String(text ?? "");
    const blocks = getDelimitedCalculatorBlocks(raw);
    if (blocks.length === 0) return raw;

    let cursor = 0;
    let out = "";
    blocks.forEach((block, blockIndex) => {
        out += raw.slice(cursor, block.index);
        out += String(replacer(block.expression, block, blockIndex) ?? "");
        cursor = block.endIndex;
    });
    out += raw.slice(cursor);
    return out;
};

export const parseDelimitedCalculatorExpression = (text: string): ParsedCalculatorExpression => {
    const trimmed = String(text ?? "").trim();
    if (!trimmed) {
        return {hasDelimitedExpression: false, expression: "", delimiter: null};
    }

    if (
        trimmed.startsWith(CALCULATOR_DELIMITER_OPEN) &&
        trimmed.endsWith(CALCULATOR_DELIMITER_CLOSE) &&
        trimmed.length >= CALCULATOR_DELIMITER_OPEN.length + CALCULATOR_DELIMITER_CLOSE.length
    ) {
        const inner = trimmed
            .slice(CALCULATOR_DELIMITER_OPEN.length, trimmed.length - CALCULATOR_DELIMITER_CLOSE.length)
            .trim();
        return {hasDelimitedExpression: true, expression: inner, delimiter: "doubleBrace"};
    }

    if (
        trimmed.startsWith(LEGACY_CALCULATOR_DELIMITER_OPEN) &&
        trimmed.endsWith(LEGACY_CALCULATOR_DELIMITER_CLOSE) &&
        trimmed.length >= LEGACY_CALCULATOR_DELIMITER_OPEN.length + LEGACY_CALCULATOR_DELIMITER_CLOSE.length
    ) {
        const inner = trimmed
            .slice(LEGACY_CALCULATOR_DELIMITER_OPEN.length, trimmed.length - LEGACY_CALCULATOR_DELIMITER_CLOSE.length)
            .trim();
        return {hasDelimitedExpression: true, expression: inner, delimiter: "legacyDollar"};
    }

    if (trimmed.startsWith("{") && trimmed.endsWith("}") && trimmed.length >= 2) {
        const inner = trimmed.slice(1, -1).trim();
        return {hasDelimitedExpression: true, expression: inner, delimiter: "brace"};
    }

    return {hasDelimitedExpression: false, expression: "", delimiter: null};
};

export const wrapCalculatorExpression = (expression: string): string =>
    `${CALCULATOR_DELIMITER_OPEN}${String(expression ?? "").trim()}${CALCULATOR_DELIMITER_CLOSE}`;

export const getCalculatorMeta = (engine: CreativeEngine, blockId: number): CalculatorMeta | null => {
    let raw = "";
    try {
        raw = engine.block.getMetadata?.(blockId, CALCULATOR_META_KEY) ?? "";
    } catch {
        raw = "";
    }
    if (!raw) return null;
    try {
        return withCalculatorDefaults(JSON.parse(raw));
    } catch {
        return null;
    }
};

export const setCalculatorMeta = (engine: CreativeEngine, blockId: number, meta: CalculatorMeta): void => {
    try {
        engine.block.setMetadata?.(blockId, CALCULATOR_META_KEY, JSON.stringify(withCalculatorDefaults(meta)));
    } catch {}
};

type EvaluationResult = {
    ok: boolean;
    display: string;
    rawValue: string;
    error?: string;
};

export type CalculatorEvaluationResult = EvaluationResult;

const calculatorTemplateTextByEngine = new WeakMap<CreativeEngine, Map<number, string>>();

const getCalculatorTemplateTextMap = (engine: CreativeEngine): Map<number, string> => {
    let map = calculatorTemplateTextByEngine.get(engine);
    if (!map) {
        map = new Map<number, string>();
        calculatorTemplateTextByEngine.set(engine, map);
    }
    return map;
};

const getCalculatorTemplateText = (engine: CreativeEngine, blockId: number, currentText: string): string => {
    const cache = getCalculatorTemplateTextMap(engine);
    if (hasDelimitedCalculatorBlocks(currentText)) {
        cache.set(blockId, currentText);
        return currentText;
    }
    const cached = cache.get(blockId);
    if (cached && hasDelimitedCalculatorBlocks(cached)) {
        return cached;
    }
    return currentText;
};

export const getCalculatorTemplateTextForBlock = (
    engine: CreativeEngine,
    blockId: number,
    currentText?: string
): string => {
    const nextCurrentText = currentText ?? getTextBlockText(engine, blockId);
    return getCalculatorTemplateText(engine, blockId, nextCurrentText);
};

export const getTextVariableReferenceKindForTemplateText = (
    templateText: string,
    meta?: Partial<CalculatorMeta> | null
): TextVariableReferenceKind => {
    if (parseDelimitedCalculatorExpression(templateText).hasDelimitedExpression) {
        return "numeric";
    }

    if (hasDelimitedCalculatorBlocks(templateText)) {
        return "display";
    }

    const normalizedMeta = withCalculatorDefaults(meta ?? undefined);
    if (normalizedMeta.enabled && normalizedMeta.expression.trim() !== "") {
        return "numeric";
    }

    return "display";
};

export const getNamedTextBlockReferenceKind = (
    engine: CreativeEngine,
    blockId: number,
    currentText?: string
): TextVariableReferenceKind => {
    const nextCurrentText = currentText ?? getTextBlockText(engine, blockId);
    const templateText = getCalculatorTemplateText(engine, blockId, nextCurrentText);
    const meta = withCalculatorDefaults(getCalculatorMeta(engine, blockId));
    return getTextVariableReferenceKindForTemplateText(templateText, meta);
};

export const getNamedTextBlockVariableDescriptor = (
    engine: CreativeEngine | null | undefined,
    blockId: number
): TextVariableDescriptor | null => {
    if (!engine || !engine.block.isValid?.(blockId) || !isTextBlock(engine, blockId)) {
        return null;
    }

    const meta = withCalculatorDefaults(getCalculatorMeta(engine, blockId));
    const name = String(meta.varName ?? "").trim();
    if (!name) return null;

    return {
        name,
        referenceKind: getNamedTextBlockReferenceKind(engine, blockId)
    };
};

const getTextVariableReferenceKindKey = (name: string): string =>
    `text:${String(name ?? "").trim()}:referenceKind`;

const normalizeTextVariableReferenceKind = (value: unknown): TextVariableReferenceKind | undefined => {
    if (value === "numeric" || value === "display") {
        return value;
    }
    return undefined;
};

const buildNamedTextBlockVariableState = (
    engine: CreativeEngine,
    blockId: number,
    currentText?: string
): { displayValue: string; numericValue: string; referenceKind: TextVariableReferenceKind } => {
    const meta = withCalculatorDefaults(getCalculatorMeta(engine, blockId));
    const nextCurrentText = currentText ?? getTextBlockText(engine, blockId);
    const templateText = getCalculatorTemplateText(engine, blockId, nextCurrentText);
    const delimitedBlocks = getDelimitedCalculatorBlocks(templateText);

    if (delimitedBlocks.length > 0) {
        const referenceKind = getTextVariableReferenceKindForTemplateText(templateText, meta);
        let firstRawValue = "";
        let hasFirstRawValue = false;
        const displayValue = replaceDelimitedCalculatorBlocks(
            templateText,
            (expression, _block, blockIndex) => {
                const result = evaluateCalculatorExpression(engine, expression, meta);
                if (!result.ok) {
                    return "";
                }
                if (!hasFirstRawValue || blockIndex === 0) {
                    firstRawValue = result.rawValue;
                    hasFirstRawValue = true;
                }
                return result.display;
            }
        );

        return {
            displayValue,
            numericValue: referenceKind === "numeric" && hasFirstRawValue ? firstRawValue : "",
            referenceKind
        };
    }

    if (meta.enabled && meta.expression.trim() !== "") {
        const result = evaluateCalculatorExpression(engine, meta.expression, meta);
        return {
            displayValue: result.ok ? result.display : "",
            numericValue: result.ok ? result.rawValue : "",
            referenceKind: "numeric"
        };
    }

    return {
        displayValue: nextCurrentText,
        numericValue: "",
        referenceKind: "display"
    };
};

export const evaluateCalculatorsForPage = (engine: CreativeEngine, pageId: number | null | undefined): void => {
    if (!engine || !pageId || !engine.block.isValid?.(pageId)) return;

    const blocks = findCalculatorBlocksForPage(engine, pageId);
    if (blocks.length === 0) return;

    const displayByBlock = new Map<number, string>();

    for (let pass = 0; pass < CALCULATOR_PASS_LIMIT; pass += 1) {
        let anyVarChanged = false;

        for (const blockId of blocks) {
            const meta = withCalculatorDefaults(getCalculatorMeta(engine, blockId));
            const varName = String(meta.varName ?? "").trim();
            const currentText = getTextBlockText(engine, blockId);
            const templateText = getCalculatorTemplateText(engine, blockId, currentText);
            const delimitedBlocks = getDelimitedCalculatorBlocks(templateText);

            if (delimitedBlocks.length > 0) {
                const firstExpression = delimitedBlocks[0].expression;
                if (!meta.enabled || meta.expression !== firstExpression) {
                    setCalculatorMeta(engine, blockId, {
                        ...meta,
                        enabled: true,
                        expression: firstExpression
                    });
                }

                let firstRawValue = "";
                let hasFirstRawValue = false;
                const display = replaceDelimitedCalculatorBlocks(
                    templateText,
                    (expression, _block, blockIndex) => {
                        const result = evaluateCalculatorExpression(engine, expression, meta);
                        if (!result.ok) {
                            return "";
                        }
                        if (!hasFirstRawValue || blockIndex === 0) {
                            firstRawValue = result.rawValue;
                            hasFirstRawValue = true;
                        }
                        return result.display;
                    }
                );

                displayByBlock.set(blockId, display);

                if (varName) {
                    writeVariableByKey(engine, `text:${varName}`, display);
                    const referenceKind = parseDelimitedCalculatorExpression(templateText).hasDelimitedExpression ? "numeric" : "display";
                    writeVariableByKey(engine, getTextVariableReferenceKindKey(varName), referenceKind);

                    const key = `calc:${varName}`;
                    const nextValue = referenceKind === "numeric" && hasFirstRawValue ? firstRawValue : "";
                    const previous = readVariableByKey(engine, key);
                    if (previous !== nextValue) {
                        try {
                            engine.variable?.setString?.(key, nextValue);
                            anyVarChanged = true;
                        } catch {}
                    }
                }
                continue;
            }

            const expression = resolveExpressionForBlock(engine, blockId, meta);

            if (!expression) {
                if (varName) {
                    const key = `calc:${varName}`;
                    const previous = readVariableByKey(engine, key);
                    if (previous !== "") {
                        try {
                            engine.variable?.setString?.(key, "");
                            anyVarChanged = true;
                        } catch {}
                    }
                }
                continue;
            }

            const result = evaluateCalculatorExpression(engine, expression, meta);
            if (!result.ok) {
                // Keep dependent calculations from using stale values after expression errors.
                if (varName) {
                    const key = `calc:${varName}`;
                    const previous = readVariableByKey(engine, key);
                    if (previous !== "") {
                        try {
                            engine.variable?.setString?.(key, "");
                            anyVarChanged = true;
                        } catch {}
                    }
                }
                continue;
            }

            displayByBlock.set(blockId, result.display);

            if (!varName) continue;

            writeVariableByKey(engine, `text:${varName}`, result.display);
            writeVariableByKey(engine, getTextVariableReferenceKindKey(varName), "numeric");

            const key = `calc:${varName}`;
            const previous = readVariableByKey(engine, key);
            if (previous !== result.rawValue) {
                try {
                    engine.variable?.setString?.(key, result.rawValue);
                    anyVarChanged = true;
                } catch {}
            }
        }

        if (!anyVarChanged) break;
    }

    displayByBlock.forEach((display, blockId) => {
        const current = getTextBlockText(engine, blockId);
        if (current !== display) {
            setTextBlockText(engine, blockId, display);
        }
    });
};

export const evaluateCalculatorForExpression = (
    engine: CreativeEngine,
    expressionRaw: string,
    meta?: Partial<CalculatorMeta> | null
): CalculatorEvaluationResult => {
    const normalizedMeta = withCalculatorDefaults(meta ?? undefined);
    return evaluateCalculatorExpression(engine, expressionRaw, normalizedMeta);
};

export const findAllNamedTextBlocks = (engine: CreativeEngine | null | undefined): number[] => {
    if (!engine) return [];

    const pages: number[] = engine.scene?.getPages?.() ?? [];
    const out: number[] = [];
    const stack: number[] = [...pages];

    while (stack.length > 0) {
        const id = stack.pop()!;
        if (!engine.block.isValid?.(id)) continue;

        if (isTextBlock(engine, id)) {
            const meta = withCalculatorDefaults(getCalculatorMeta(engine, id));
            if (String(meta.varName ?? "").trim() !== "") {
                out.push(id);
            }
        }

        try {
            const children = engine.block.getChildren?.(id) ?? [];
            for (const childId of children) stack.push(childId);
        } catch {}

        try {
            if (engine.block.supportsFill?.(id)) {
                const fill = engine.block.getFill?.(id);
                if (fill != null) stack.push(fill);
            }
        } catch {}
    }

    return out;
};

export const syncNamedTextBlockVariablesForScene = (engine: CreativeEngine | null | undefined): void => {
    if (!engine) return;

    const ids = findAllNamedTextBlocks(engine);
    ids.forEach((id) => {
        const meta = withCalculatorDefaults(getCalculatorMeta(engine, id));
        const varName = String(meta.varName ?? "").trim();
        if (!varName) return;

        const currentText = getTextBlockText(engine, id);
        const nextState = buildNamedTextBlockVariableState(engine, id, currentText);
        writeVariableByKey(engine, `text:${varName}`, nextState.displayValue);
        writeVariableByKey(engine, `calc:${varName}`, nextState.numericValue);
        writeVariableByKey(engine, getTextVariableReferenceKindKey(varName), nextState.referenceKind);
    });
};

const findCalculatorBlocksForPage = (engine: CreativeEngine, pageId: number): number[] => {
    const out: number[] = [];
    const stack: number[] = [pageId];

    while (stack.length > 0) {
        const id = stack.pop()!;
        if (!engine.block.isValid?.(id)) continue;

        if (isTextBlock(engine, id)) {
            const text = getTextBlockText(engine, id);
            const meta = withCalculatorDefaults(getCalculatorMeta(engine, id));
            if (hasDelimitedCalculatorBlocks(text) || (meta.enabled && meta.expression.trim() !== "")) {
                out.push(id);
            }
        }

        try {
            const children = engine.block.getChildren?.(id) ?? [];
            for (const childId of children) stack.push(childId);
        } catch {}

        try {
            if (engine.block.supportsFill?.(id)) {
                const fill = engine.block.getFill?.(id);
                if (fill != null) stack.push(fill);
            }
        } catch {}
    }

    return out;
};

const resolveExpressionForBlock = (engine: CreativeEngine, blockId: number, meta: CalculatorMeta): string => {
    const text = getTextBlockText(engine, blockId);
    const parsed = parseDelimitedCalculatorExpression(text);

    if (parsed.hasDelimitedExpression) {
        if (!meta.enabled || meta.expression !== parsed.expression) {
            setCalculatorMeta(engine, blockId, {
                ...meta,
                enabled: true,
                expression: parsed.expression
            });
        }
        return parsed.expression;
    }

    if (meta.enabled && meta.expression.trim() !== "") {
        return meta.expression;
    }

    return "";
};

const evaluateCalculatorExpression = (engine: CreativeEngine, expressionRaw: string, meta: CalculatorMeta): EvaluationResult => {
    const expression = normalizeExpression(expressionRaw);
    if (!expression) {
        return {ok: false, display: "", rawValue: ""};
    }

    const displayOnlyReferences = extractVariableReferencesFromExpression(expressionRaw)
        .filter((name) => readTextVariableReferenceKindByName(engine, name) === "display");
    if (displayOnlyReferences.length > 0) {
        return {
            ok: false,
            display: "",
            rawValue: "",
            error: formatDisplayOnlyVariableCalculationError(displayOnlyReferences)
        };
    }

    const identifiers = extractSymbols(expression);
    const scope: Record<string, unknown> = {
        v: (name: string) => readDynamicVariableNumber(engine, String(name ?? "").trim()),
        value: (name: string) => readDynamicVariableNumber(engine, String(name ?? "").trim())
    };

    identifiers.forEach((name) => {
        scope[name] = readDynamicVariableNumber(engine, name);
    });

    let evaluated: unknown;
    try {
        evaluated = math.evaluate(expression, scope);
    } catch {
        return {ok: false, display: "", rawValue: ""};
    }

    const numeric = typeof evaluated === "number" ? evaluated : Number(evaluated);
    const hasNumeric = Number.isFinite(numeric);
    const rawValue = hasNumeric ? String(numeric) : String(evaluated ?? "");
    const display = formatCalculatorValue(hasNumeric ? numeric : evaluated, meta);

    return {ok: true, display, rawValue};
};

const LOOSE_OPERATOR_SPLIT = /\s([+\-*/%^])\s/;
const NUMERIC_LITERAL_PATTERN = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;

const escapeMathStringLiteral = (value: string): string =>
    String(value ?? "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"');

const isLikelyLooseBracketExpression = (value: string): boolean => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return false;
    if (!LOOSE_OPERATOR_SPLIT.test(trimmed)) return false;

    const parts = trimmed.split(LOOSE_OPERATOR_SPLIT);
    if (parts.length < 3) return false;

    const operands = parts
        .filter((_part, index) => index % 2 === 0)
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

    return operands.some((operand) => NUMERIC_LITERAL_PATTERN.test(operand));
};

const normalizeLooseBracketExpression = (value: string): string => {
    const parts = String(value ?? "").trim().split(LOOSE_OPERATOR_SPLIT);
    return parts
        .map((part, index) => {
            if (index % 2 === 1) {
                return String(part ?? "").trim();
            }
            const operand = String(part ?? "").trim();
            if (!operand) return "0";
            if (NUMERIC_LITERAL_PATTERN.test(operand)) return operand;
            if (/[()]/.test(operand)) return operand;
            return `v("${escapeMathStringLiteral(operand)}")`;
        })
        .join(" ");
};

const extractVariableNamesFromLooseBracketExpression = (value: string): string[] => {
    const parts = String(value ?? "").trim().split(LOOSE_OPERATOR_SPLIT);
    const names = new Set<string>();

    parts.forEach((part, index) => {
        if (index % 2 === 1) return;
        const operand = String(part ?? "").trim();
        if (!operand) return;
        if (NUMERIC_LITERAL_PATTERN.test(operand)) return;
        if (/[()]/.test(operand)) return;
        names.add(operand);
    });

    return Array.from(names);
};

export const extractVariableReferencesFromExpression = (expressionRaw: string): string[] => {
    const names = new Set<string>();
    const raw = String(expressionRaw ?? "");
    const matches = raw.matchAll(/\[([^\]]+)\]/g);

    for (const match of matches) {
        const key = String(match?.[1] ?? "").trim();
        if (!key) continue;
        if (isLikelyLooseBracketExpression(key)) {
            extractVariableNamesFromLooseBracketExpression(key).forEach((name) => names.add(name));
            continue;
        }
        names.add(key);
    }

    return Array.from(names);
};

export const formatDisplayOnlyVariableCalculationError = (names: string[]): string => {
    const uniqueNames = Array.from(new Set(names.map((name) => String(name ?? "").trim()).filter(Boolean)));
    if (uniqueNames.length === 0) return "Display-only variables cannot be used in calculations.";
    if (uniqueNames.length === 1) {
        return `${uniqueNames[0]} is display-only and cannot be used in calculations.`;
    }
    return `${uniqueNames.join(", ")} are display-only and cannot be used in calculations.`;
};

const normalizeExpression = (rawExpression: string): string => {
    const trimmed = String(rawExpression ?? "").trim();
    if (!trimmed) return "";
    const parsed = parseDelimitedCalculatorExpression(trimmed);
    const unwrapped = parsed.hasDelimitedExpression ? parsed.expression : trimmed;
    if (!unwrapped) return "";
    return unwrapped.replace(/\[([^\]]+)\]/g, (_match, token) => {
        const key = String(token ?? "").trim();
        if (!key) return "0";
        if (isLikelyLooseBracketExpression(key)) {
            return normalizeLooseBracketExpression(key);
        }
        return `v("${escapeMathStringLiteral(key)}")`;
    });
};

const extractSymbols = (expression: string): string[] => {
    const names = new Set<string>();
    try {
        const parsed = math.parse(expression);
        parsed.traverse((node: any, _path: string, parent: any) => {
            if (!node?.isSymbolNode) return;
            const name = String(node.name ?? "").trim();
            if (!name) return;
            if (RESERVED_SYMBOLS.has(name)) return;
            // If this symbol is the function name in a call, keep it out of the variable scope.
            if (parent?.isFunctionNode && parent.fn === node) return;
            names.add(name);
        });
    } catch {
        return [];
    }
    return Array.from(names);
};

const readVariableByKey = (engine: CreativeEngine | null | undefined, key: string): string => {
    if (!engine) return "";
    try {
        return String(engine.variable?.getString?.(key) ?? "");
    } catch {
        return "";
    }
};

const writeVariableByKey = (
    engine: CreativeEngine | null | undefined,
    key: string,
    value: string
): boolean => {
    const previous = readVariableByKey(engine, key);
    if (previous === value) return false;
    try {
        engine?.variable?.setString?.(key, value);
        return true;
    } catch {
        return false;
    }
};

const readTextVariableReferenceKindByName = (
    engine: CreativeEngine | null | undefined,
    name: string
): TextVariableReferenceKind | undefined =>
    normalizeTextVariableReferenceKind(readVariableByKey(engine, getTextVariableReferenceKindKey(name)));

const readDynamicVariableState = (
    engine: CreativeEngine | null | undefined,
    name: string,
    candidates: Array<{ valueKey: string; typeKey?: string }>
): DynamicVariableState => {
    if (!name) return { value: "" };

    for (const candidate of candidates) {
        const value = readVariableByKey(engine, candidate.valueKey);
        if (value.trim() === "") continue;
        const valueType = candidate.typeKey
            ? normalizeDynamicValueType(readVariableByKey(engine, candidate.typeKey))
            : undefined;
        return { value, valueType };
    }

    return readWindowDynamicVariableEntry(name) ?? { value: "" };
};

const NUMERIC_DYNAMIC_VARIABLE_CANDIDATES = (name: string): Array<{ valueKey: string; typeKey?: string }> => [
    { valueKey: `input:${name}` },
    { valueKey: `calc:${name}` },
    { valueKey: `text:${name}` },
    { valueKey: `slider:${name}:value` },
    { valueKey: `choice:${name}`, typeKey: `choice:${name}:type` },
    { valueKey: name, typeKey: `${name}:type` }
];

const DISPLAY_DYNAMIC_VARIABLE_CANDIDATES = (name: string): Array<{ valueKey: string; typeKey?: string }> => [
    { valueKey: `input:${name}` },
    { valueKey: `text:${name}` },
    { valueKey: `calc:${name}` },
    { valueKey: `slider:${name}:value` },
    { valueKey: `choice:${name}`, typeKey: `choice:${name}:type` },
    { valueKey: name, typeKey: `${name}:type` }
];

export const readDynamicVariableValue = (
    engine: CreativeEngine | null | undefined,
    name: string
): string => {
    return readDynamicVariableState(engine, name, NUMERIC_DYNAMIC_VARIABLE_CANDIDATES(name)).value;
};

export const readDynamicVariableDisplayValue = (
    engine: CreativeEngine | null | undefined,
    name: string
): string => {
    return readDynamicVariableState(engine, name, DISPLAY_DYNAMIC_VARIABLE_CANDIDATES(name)).value;
};

const coerceToNumber = (raw: unknown): number => {
    if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
    if (typeof raw !== "string") return 0;
    const direct = Number(raw);
    if (Number.isFinite(direct)) return direct;
    const cleaned = raw.replace(/[^0-9.\-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
};

const coerceDynamicVariableToNumber = (state: DynamicVariableState): number =>
    coerceToNumber(state.value);

const readDynamicVariableNumber = (
    engine: CreativeEngine | null | undefined,
    name: string
): number => coerceDynamicVariableToNumber(readDynamicVariableState(engine, name, NUMERIC_DYNAMIC_VARIABLE_CANDIDATES(name)));

const normalizeOutputType = (value: unknown): CalculatorOutputType => {
    if (value === "currency" || value === "percent" || value === "number") {
        return value;
    }
    return "number";
};

const formatCalculatorValue = (result: unknown, meta: CalculatorMeta): string => {
    const outputType = normalizeOutputType(meta.outputType);
    const decimals = clampDecimals(meta.decimals);
    const useGrouping = meta.useThousandsSeparator !== false;

    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping
    });

    const numeric = typeof result === "number" ? result : Number(result);
    const hasNumeric = Number.isFinite(numeric);

    let value = "";
    if (hasNumeric) {
        if (outputType === "percent") {
            value = `${formatter.format(numeric * 100)}%`;
        } else if (outputType === "currency") {
            const symbol = meta.currencySymbol || "$";
            value = `${symbol}${formatter.format(numeric)}`;
        } else {
            value = formatter.format(numeric);
        }
    } else {
        value = "";
    }

    const prefix = meta.prefix ?? "";
    const suffix = meta.suffix ?? "";
    return `${prefix}${value}${suffix}`;
};

const clampDecimals = (value: number): number => {
    if (!Number.isFinite(value)) return 2;
    return Math.max(0, Math.min(MAX_DECIMALS, Math.round(value)));
};
